import { useEffect, useRef, useState } from 'react';
import { fetchLiveState } from '../services/openF1LiveClient.js';

/**
 * useLiveSession — polls OpenF1 for whether an F1 session is happening now.
 * -------------------------------------------------------------------------
 * Drives the floating LIVE pill: it appears only when status is 'live' or
 * 'locked', and hides when 'offline'. Kept deliberately cheap:
 *   - polls every POLL_MS (45s) — session windows change on the minute scale
 *   - pauses while the tab is hidden (Page Visibility) to spare quota/battery,
 *     and refreshes immediately on re-focus
 *   - one in-flight request at a time, aborted on unmount
 *
 * @returns {{status:'loading'|'live'|'locked'|'offline', session:object|null}}
 */
const POLL_MS = 45_000;

export default function useLiveSession() {
  const [state, setState] = useState({ status: 'loading', session: null });
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let controller = null;

    async function poll() {
      controller?.abort();
      controller = new AbortController();
      try {
        const next = await fetchLiveState({ signal: controller.signal });
        if (!cancelled) setState({ status: next.status, session: next.session });
      } catch (err) {
        if (!cancelled && err?.name !== 'AbortError') {
          setState({ status: 'offline', session: null });
        }
      }
    }

    function schedule() {
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        if (!document.hidden) poll();
      }, POLL_MS);
    }

    function onVisibility() {
      if (!document.hidden) poll();
    }

    poll();
    schedule();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      controller?.abort();
      clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return state;
}
